import { useAbortableConfirm } from "../lib/useAbortableConfirm";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { PlayerProfileScopeSwitcher } from "../components/PlayerFamilyProfileSwitcher";
import { makeLatestOnly } from "../lib/latestOnly";
import { usePlayerProfileScope } from "../lib/usePlayerProfileScope";
import {
  api,
  ApiError,
  openAuthenticatedFile,
  type PlayerClubApplication,
  type PlayerClubBrief,
} from "../lib/api";
import { uploadAssetHref } from "../lib/assets";
import { membershipPaymentStatusLabel, membershipRequestStatusLabel } from "../lib/labels";
import { formatDate } from "../lib/dateFormat";
import { formatMoney } from "../lib/money";
import { ClubActivateContent } from "./club/ClubActivatePage";
import PlayerMiClub from "./club/PlayerMiClub";
import "./club/PlayerMiClub.css";

function requestDateLabel(value: string | null): string {
  return formatDate(value, "Sin fecha");
}

function isMember(club: PlayerClubBrief): boolean {
  return Boolean(club.relationship_status?.startsWith("member_"));
}

function hasPendingRequest(club: PlayerClubBrief): boolean {
  return club.relationship_status === "request_pending";
}

function clubLocationLabel(club: PlayerClubBrief): string {
  return [club.neighborhood, club.city, club.state].filter(Boolean).join(" · ") || "Ubicación por definir";
}

function clubSearchText(club: PlayerClubBrief): string {
  return [
    club.name,
    club.neighborhood,
    club.city,
    club.state,
    club.address_line,
    club.contact_phone,
    club.contact_email,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function foldText(value: string): string {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

function playerDocumentPath(src: string, label: string, back: string): string {
  const params = new URLSearchParams({ src, label, back });
  return `/player/documento?${params.toString()}`;
}

export default function PlayerClubPage() {
  const { token, user } = useAuth();
  // Organizador sin perfil de jugador: la API responde 409; en su lugar se
  // explica cómo activar un club (etapa 1).
  const isOrganizer = Boolean(user?.roles?.some((role) => role === "admin" || role === "superadmin"));
  const [noPlayerProfile, setNoPlayerProfile] = useState(false);
  const [myClubs, setMyClubs] = useState<PlayerClubBrief[]>([]);
  const [availableClubs, setAvailableClubs] = useState<PlayerClubBrief[]>([]);
  const [requests, setRequests] = useState<PlayerClubApplication[]>([]);
  const [search, setSearch] = useState("");
  const [city, setCity] = useState("");
  const [cities, setCities] = useState<string[]>([]);
  const [selectedClubId, setSelectedClubId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [uploadingOrderId, setUploadingOrderId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  // Cuenta familiar (Eduardo 2026-09-29): clubes, solicitudes y pagos son
  // del hijo elegido en «Perfil activo», no del primero de la cuenta.
  const scope = usePlayerProfileScope();
  const scopeProfileId = scope.profileId;
  const { askConfirm } = useAbortableConfirm([scopeProfileId]);
  const latestLoad = useMemo(() => makeLatestOnly(), []);
  const [loadedProfileId, setLoadedProfileId] = useState<number | null | undefined>(undefined);

  const loadMarketplace = useCallback(async () => {
    if (!token || !scope.ready) return;
    const attempt = latestLoad.begin();
    setError(null);
    try {
      const resp = await api.clubs.playerClubs(token, scopeProfileId);
      if (!latestLoad.isCurrent(attempt)) return;
      const owned = resp.data.my_clubs ?? resp.data.clubs ?? [];
      setMyClubs(owned);
      setAvailableClubs(resp.data.available_clubs ?? owned);
      setRequests(resp.data.requests ?? []);
      setCities(resp.data.cities ?? []);
      setLoadedProfileId(scopeProfileId);
    } catch (err) {
      if (!latestLoad.isCurrent(attempt)) return;
      if (err instanceof ApiError && err.status === 409 && isOrganizer) {
        setNoPlayerProfile(true);
        return;
      }
      setError(err instanceof ApiError ? err.message : "No pudimos cargar clubes.");
    } finally {
      if (latestLoad.isCurrent(attempt)) setLoading(false);
    }
  }, [isOrganizer, latestLoad, scope.ready, scopeProfileId, token]);
  // Mientras llegan los clubes del hijo recién elegido no se solicita nada
  // con la lista del anterior.
  const switchingProfile = loadedProfileId !== scopeProfileId;

  useEffect(() => {
    void loadMarketplace();
  }, [loadMarketplace]);

  // Buscar club (etapa 2): A→Z, sin acentos, por ciudad y por texto.
  const filteredAvailableClubs = useMemo(() => {
    const q = foldText(search.trim());
    return availableClubs
      .filter((club) => !city || foldText(club.city ?? "") === foldText(city))
      .filter((club) => !q || foldText(clubSearchText(club)).includes(q))
      .sort((a, b) => a.name.localeCompare(b.name, "es", { sensitivity: "base" }));
  }, [availableClubs, city, search]);

  async function handleApply(club: PlayerClubBrief) {
    if (!token || busy || switchingProfile || isMember(club) || hasPendingRequest(club)) return;
    const confirmed = await askConfirm(`¿Mandar solicitud de inscripción a ${club.name}?`, { acceptLabel: "Sí, mandar solicitud" });
    if (!confirmed) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await api.clubs.playerApplyToClub(
        token,
        club.id,
        { message: "Solicitud enviada desde Clubes Torneos123" },
        scopeProfileId,
      );
      setMessage(`Solicitud enviada a ${club.name}. El club podrá revisarla desde su panel.`);
      await loadMarketplace();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo enviar la solicitud.");
    } finally {
      setBusy(false);
    }
  }

  async function handleUploadReceipt(orderId: number, file: File | null | undefined) {
    if (!token || !file) return;
    setUploadingOrderId(orderId);
    setError(null);
    setMessage(null);
    try {
      await api.clubs.playerUploadMembershipReceipt(token, orderId, file);
      setMessage("Comprobante subido. El club lo revisará para activar tu membresía.");
      await loadMarketplace();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No pudimos subir el comprobante.");
    } finally {
      setUploadingOrderId(null);
    }
  }

  async function handleViewReceipt(receiptPath: string | null) {
    if (!token || !receiptPath) return;
    try {
      await openAuthenticatedFile(receiptPath, token);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo abrir el comprobante.");
    }
  }

  function renderPaymentAction(request: PlayerClubApplication) {
    const order = request.payment_order;
    if (!order) return <span className="muted">—</span>;
    if (order.status === "paid") return <span className="status-pill">{membershipPaymentStatusLabel(order.status)}</span>;
    return (
      <div className="player-club-payment-cell">
        <strong>{formatMoney(order.amount, order.currency)}</strong>
        {order.payment_instructions ? <small className="muted">{order.payment_instructions}</small> : null}
        {order.receipt_url ? (
          <button type="button" className="link-button" onClick={() => void handleViewReceipt(order.receipt_url)}>
            Ver comprobante enviado
          </button>
        ) : null}
        {(order.status === "pending_receipt" || order.status === "rejected") && (
          <label className="btn btn--compact btn--ghost">
            {uploadingOrderId === order.id ? "Subiendo…" : order.status === "rejected" ? "Subir otro" : "Subir comprobante"}
            <input
              type="file"
              accept="application/pdf,image/png,image/jpeg,image/webp"
              hidden
              disabled={uploadingOrderId === order.id}
              onChange={(event) => {
                const file = event.target.files?.[0] ?? null;
                event.currentTarget.value = "";
                void handleUploadReceipt(order.id, file);
              }}
            />
          </label>
        )}
        {order.status === "receipt_submitted" && <span className="muted">Comprobante enviado · pendiente de validación</span>}
      </div>
    );
  }

  function renderClubActions(club: PlayerClubBrief) {
    const member = isMember(club);
    const pending = hasPendingRequest(club);
    const credentialUrl = uploadAssetHref(club.credential_url);
    return (
      <div className="player-club-table-actions">
        {club.google_maps_url && (
          <a className="btn btn--compact btn--ghost" href={club.google_maps_url} target="_blank" rel="noreferrer">
            Maps
          </a>
        )}
        {member && credentialUrl ? (
          <Link className="btn btn--compact btn--ghost" to={playerDocumentPath(credentialUrl, "Credencial", "/player/clubs")}>
            Credencial
          </Link>
        ) : null}
        {member ? (
          <button type="button" className="btn btn--compact btn--ghost" onClick={() => setSelectedClubId(club.id)}>
            Socio
          </button>
        ) : club.join_mode === "invite" ? (
          <span className="mi-club__hint">Solo con invitación</span>
        ) : (
          <button
            type="button"
            className="btn btn--compact"
            disabled={busy || pending || switchingProfile}
            onClick={() => handleApply(club)}
          >
            {pending ? "Pendiente" : "Solicitar"}
          </button>
        )}
      </div>
    );
  }

  if (noPlayerProfile) {
    return <ClubActivateContent />;
  }

  if (loading) {
    return <section className="card"><p className="muted">Cargando clubes…</p></section>;
  }

  const selectedClub = myClubs.find((club) => club.id === selectedClubId) ?? myClubs[0] ?? null;
  const pendingRequests = requests.filter(
    (request) => request.status === "pending_review" || request.status === "approved_payment_pending",
  );

  return (
    <div className="mi-club">
      <header className="mi-club__header">
        <p className="mi-club__eyebrow">Mi club</p>
        <h1>{selectedClub ? selectedClub.name : "Clubes"}</h1>
        {myClubs.length > 1 ? (
          <div className="mi-club__switch" role="group" aria-label="Mis clubes">
            {myClubs.map((club) => (
              <button
                key={club.id}
                type="button"
                className="mi-club__day"
                aria-pressed={club.id === selectedClub?.id}
                onClick={() => setSelectedClubId(club.id)}
              >
                <strong>{club.name}</strong>
              </button>
            ))}
          </div>
        ) : null}
        <PlayerProfileScopeSwitcher
          scope={{
            ...scope,
            setProfileId: (profileId: number) => {
              setMessage(null);
              scope.setProfileId(profileId);
            },
          }}
          disabled={busy || uploadingOrderId !== null}
        />
        {error && <p className="form-error" role="alert">{error}</p>}
        {message && <p className="form-message" role="status">{message}</p>}
      </header>

      {selectedClub && token && !switchingProfile ? (
        <PlayerMiClub
          key={`${selectedClub.id}-${scopeProfileId ?? "yo"}`}
          token={token}
          club={selectedClub}
          profileId={scopeProfileId}
          onError={setError}
          onMessage={setMessage}
        />
      ) : null}

      {pendingRequests.length > 0 ? (
        <section className="mi-club__card" aria-label="Solicitudes pendientes">
          <h2 className="mi-club__title">Solicitudes</h2>
          <ul className="mi-club__reservations">
            {pendingRequests.map((request) => (
              <li key={request.id} className="mi-club__reservation mi-club__reservation--stack">
                <div className="mi-club__reservation-info">
                  <strong>{request.club?.name ?? `Club #${request.club_id}`}</strong>
                  <span>
                    {request.status === "pending_review"
                      ? "Solicitud enviada · esperando aprobación del club"
                      : membershipRequestStatusLabel(request.status)}
                    {" · "}
                    {requestDateLabel(request.created_at)}
                  </span>
                </div>
                {request.payment_order ? renderPaymentAction(request) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="mi-club__card mi-club__search" id="buscar-club" aria-label="Buscar club">
        {!selectedClub ? (
          <>
            <h2 className="mi-club__title">¿Aún no eres socio de un club?</h2>
            <p className="mi-club__line">Busca tu club y envía tu solicitud. El club la revisa y te avisa.</p>
          </>
        ) : (
          <h2 className="mi-club__title">Buscar club</h2>
        )}
        <div className="mi-club__filters">
          <label className="mi-club__field">
            <span>Buscar</span>
            <input
              type="search"
              value={search}
              placeholder="Nombre, colonia o ciudad"
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
          <label className="mi-club__field">
            <span>Ciudad</span>
            <select className="t123-select" value={city} onChange={(event) => setCity(event.target.value)}>
              <option value="">Todas</option>
              {cities.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </label>
        </div>
        {filteredAvailableClubs.length === 0 ? (
          <p className="mi-club__hint">No encontramos clubes con ese filtro.</p>
        ) : (
          <ul className="mi-club__directory">
            {filteredAvailableClubs.map((club) => (
              <li key={club.id} className="mi-club__club">
                <div className="mi-club__reservation-info">
                  <strong>{club.name}</strong>
                  <span>{clubLocationLabel(club)} · {club.courts_count ?? 0} {club.courts_count === 1 ? "cancha" : "canchas"}</span>
                </div>
                {renderClubActions(club)}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
